import { useCallback, useRef } from "react"
import { chatAxios } from "../../utils/aiService"

export interface SSEMessage {
  /** Raw `data:` payload (joined with `\n` when multiple lines are present). */
  data: string
  /** Optional `event:` field. */
  event?: string
  /** Optional `id:` field. */
  id?: string
  /** Optional `retry:` field (ms). */
  retry?: number
}

export interface SSERequestOptions<TBody = unknown> {
  url: string
  body?: TBody
  headers?: Record<string, string>
  signal?: AbortSignal
  onmessage: (msg: SSEMessage) => void
  onclose?: () => void
  onerror?: (err: unknown) => void
}

function normalizeNewlines(input: string): string {
  // Servers may send \r\n; normalise so splitting is consistent.
  return input.replace(/\r\n/g, "\n")
}

function parseSseFrames(text: string): {
  messages: SSEMessage[]
  rest: string
} {
  const normalized = normalizeNewlines(text)
  const messages: SSEMessage[] = []

  // SSE frames are separated by a blank line.
  const parts = normalized.split("\n\n")
  const rest = parts.pop() ?? ""

  for (const frame of parts) {
    // Skip keepalive empty frames.
    if (!frame.trim()) continue

    let event: string | undefined
    let id: string | undefined
    let retry: number | undefined
    const dataLines: string[] = []

    for (const rawLine of frame.split("\n")) {
      const line = rawLine.trimEnd()
      if (!line) continue
      if (line.startsWith(":")) continue // comment / keepalive

      const colonIdx = line.indexOf(":")
      const field = colonIdx === -1 ? line : line.slice(0, colonIdx)
      // Per spec, a single leading space after ':' is ignored.
      const value =
        colonIdx === -1 ? "" : line.slice(colonIdx + 1).replace(/^ /, "")

      switch (field) {
        case "event":
          event = value
          break
        case "data":
          dataLines.push(value)
          break
        case "id":
          id = value
          break
        case "retry": {
          const n = Number(value)
          if (Number.isFinite(n)) retry = n
          break
        }
        default:
          break
      }
    }

    messages.push({
      data: dataLines.join("\n"),
      event,
      id,
      retry,
    })
  }

  return { messages, rest }
}

/**
 * Axios-based SSE over HTTP POST.
 *
 * Uses the `fetch` adapter (configured on `chatAxios`) so the response body is
 * exposed as a `ReadableStream<Uint8Array>` via `response.data` when
 * `responseType: "stream"`. The XHR adapter is unsuitable here because it
 * resolves before flushing the trailing `progress` event, so the final bytes
 * of a stream can be lost. With the fetch adapter, `onDownloadProgress` only
 * carries cumulative byte counts (no chunk data), so we read the stream
 * directly instead.
 */
export function useSSE() {
  const bufferRef = useRef("")

  const postEventStream = useCallback(
    async <TBody>({
      url,
      body,
      signal,
      onmessage,
      onclose,
      onerror,
    }: SSERequestOptions<TBody>): Promise<void> => {
      bufferRef.current = ""

      const dispatchFrames = (text: string): void => {
        bufferRef.current += text
        const { messages, rest } = parseSseFrames(bufferRef.current)
        bufferRef.current = rest
        for (const msg of messages) {
          onmessage(msg)
        }
      }

      try {
        const response = await chatAxios.request<ReadableStream<Uint8Array>>({
          url,
          method: "POST",
          data: body,
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
          },
          signal,
          responseType: "stream",
        })

        const stream = response.data

        const reader = stream.getReader()
        const decoder = new TextDecoder("utf-8")

        try {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            dispatchFrames(decoder.decode(value, { stream: true }))
          }

          // Flush any bytes still held inside the decoder (e.g. a multi-byte
          // UTF-8 char split across the last two chunks).
          const tail = decoder.decode()
          if (tail) {
            dispatchFrames(tail)
          }

          // Some SSE producers terminate without a final `\n\n` separator.
          // Emit the residual buffer as one last frame so we don't drop the
          // final event when the connection closes cleanly.
          const trailing = bufferRef.current
          bufferRef.current = ""
          if (trailing.trim()) {
            const { messages } = parseSseFrames(`${trailing}\n\n`)
            for (const msg of messages) {
              onmessage(msg)
            }
          }
        } finally {
          reader.releaseLock()
        }

        onclose?.()
      } catch (err: unknown) {
        if (signal?.aborted) {
          // Match fetchEventSource behavior: abort should be silent/handled by caller.
          const abortError = new DOMException("Aborted", "AbortError")
          throw abortError
        }

        onerror?.(err)

        throw err
      } finally {
        bufferRef.current = ""
      }
    },
    []
  )

  return { postEventStream }
}
